import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mutate } from 'swr';

import { PeopleSVG } from 'src/assets/svg';
import { Button } from 'src/components/Buttons/Button';
import Input from 'src/components/FormComponents/Input';
import { EnvelopeIcon, PhoneIcon } from 'src/components/Icons/icons';
import { ScrollableModal } from 'src/components/Modals/ScrollableModal';
import { useNotification } from 'src/components/Notification/useNotfication';
import { Section } from 'src/components/Section';
import container from 'src/container';
import { formatPhoneNumber } from 'src/utils/stringUtils';

const {
    cradle: { munduClient },
} = container;

type Props = {
    organizationId: string;
    originalAdministrator: {
        profile: {
            firstName: string;
            lastName: string;
        };
        email?: string;
        phoneNumber?: string;
        avatarUrl?: string;
    };
    canDelete: boolean;
    onClose: () => void;
};

export const ReplaceLegacyAdministratorModal = ({
    organizationId,
    originalAdministrator,
    canDelete,
    onClose,
}: Props) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [confirmationName, setConfirmationName] = useState('');
    const originalAdministratorName = `${originalAdministrator.profile.firstName} ${originalAdministrator.profile.lastName}`;
    const canConfirmDeletion =
        confirmationName.trim() === originalAdministratorName;

    const deleteAdministrator = async () => {
        try {
            setIsSubmitting(true);
            const updatedOrganization =
                await munduClient.deleteLegacyAdministrator(organizationId);
            await mutate(
                ['organization', organizationId],
                updatedOrganization,
                false,
            );
            await mutate(['creators/organizationId', organizationId]);
            notify(t('creators.legacyAdmin.success'));
            onClose();
        } catch (error) {
            if (error instanceof Error) notify(error.message, true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ScrollableModal
            visible={true}
            closeOnOutsideClick={!isSubmitting}
            onClose={onClose}
        >
            {showDeleteConfirmation ? (
                <div className="flex min-w-[18rem] max-w-md flex-col gap-6 bg-lightPeach text-woodyBrown">
                    <Section title={t('creators.legacyAdmin.title')}>
                        <div className="flex flex-col gap-5">
                            <p>
                                {t('creators.legacyAdmin.description', {
                                    name: originalAdministratorName,
                                })}
                            </p>
                            <Input
                                className="text-input"
                                label={t('creators.legacyAdmin.confirmLabel', {
                                    value: originalAdministratorName,
                                })}
                                value={confirmationName}
                                onChange={event =>
                                    setConfirmationName(
                                        event.currentTarget.value,
                                    )
                                }
                            />
                            <div className="flex flex-wrap justify-end gap-3">
                                <Button
                                    title={t('common.cancel')}
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        setConfirmationName('');
                                        setShowDeleteConfirmation(false);
                                    }}
                                />
                                <Button
                                    title={
                                        isSubmitting
                                            ? t('common.loading')
                                            : t('creators.legacyAdmin.confirm')
                                    }
                                    disabled={
                                        !canConfirmDeletion || isSubmitting
                                    }
                                    onClick={deleteAdministrator}
                                />
                            </div>
                        </div>
                    </Section>
                </div>
            ) : (
                <div className="flex min-w-[18rem] max-w-2xl flex-col gap-8 bg-lightPeach text-woodyBrown">
                    <Section title={t('creators.profile.editTitle')}>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="col-span-full flex justify-center">
                                {originalAdministrator.avatarUrl ? (
                                    <img
                                        className="mb-4 h-36 w-36 rounded-full object-cover"
                                        src={originalAdministrator.avatarUrl}
                                        alt={originalAdministratorName}
                                    />
                                ) : (
                                    <div className="mb-4 flex h-36 w-36 items-center justify-center rounded-full bg-starkWhite">
                                        <PeopleSVG className="h-[18px] w-[18px]" />
                                    </div>
                                )}
                            </div>
                            <p className="col-span-full rounded-lg bg-starkWhite p-3 text-sm">
                                {t('creators.legacyAdmin.readOnlyDescription')}
                            </p>
                            <Input
                                className="text-input"
                                label={t('profile.labels.firstName')}
                                value={originalAdministrator.profile.firstName}
                                disabled
                            />
                            <Input
                                className="text-input"
                                label={t('profile.labels.lastName')}
                                value={originalAdministrator.profile.lastName}
                                disabled
                            />
                            <Input
                                className="text-input"
                                label={t('profile.labels.phone')}
                                leadingIcon={PhoneIcon}
                                value={
                                    originalAdministrator.phoneNumber
                                        ? formatPhoneNumber(
                                              originalAdministrator.phoneNumber,
                                          )
                                        : ''
                                }
                                readOnly
                            />
                            <Input
                                className="text-input"
                                label={t('profile.labels.email')}
                                leadingIcon={EnvelopeIcon}
                                value={originalAdministrator.email ?? ''}
                                readOnly
                            />
                        </div>
                    </Section>

                    <Section
                        title={t('creators.legacyAdmin.deleteSectionTitle')}
                    >
                        <div className="flex flex-col gap-4">
                            <p>
                                {t('creators.legacyAdmin.description', {
                                    name: originalAdministratorName,
                                })}
                            </p>

                            {!canDelete ? (
                                <p className="rounded-lg bg-white p-3 text-sm">
                                    {t('creators.legacyAdmin.selfDeletion')}
                                </p>
                            ) : (
                                <div className="flex justify-end">
                                    <Button
                                        title={t(
                                            'creators.legacyAdmin.confirm',
                                        )}
                                        onClick={() =>
                                            setShowDeleteConfirmation(true)
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </Section>
                </div>
            )}
        </ScrollableModal>
    );
};
